import { useParams, Link, useLocation } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  const category = useMemo(
    () => categories.find((c) => c.slug === slug),
    [slug],
  );

  const categoryArticles = useMemo(
    () => articles.filter((a) => a.category === slug),
    [slug],
  );

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="flex flex-col items-center justify-center min-h-[60vh] pt-20 px-4">
          <h2 className="font-heading text-xl text-foreground-800 mb-3">分类未找到</h2>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            返回首页
          </Link>
        </div>
        <Footer />
      </>
    );
  }

  const pageTitle = `${category.name} - 年轻的母亲`;
  const pageDescription = category.description;

  return (
    <>
      <SEO
        title={category.name}
        description={pageDescription}
        keywords={`${category.name},年轻母亲,育儿指南,${category.name}方法`}
        canonicalUrl={`https://youngmom.readdy.ai/category/${slug}`}
      />
      <Navbar />

      <main className="pt-24 pb-16 md:pb-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <div className="mb-8">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category.name },
              ]}
            />
          </div>

          {/* Category Header */}
          <div className="mb-10">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                <i className={`${category.icon} text-xl`}></i>
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900">
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-600 mt-1">{category.description}</p>
              </div>
            </div>
          </div>

          {/* Category Tabs */}
          <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-8 scrollbar-hide">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-900'
                }`}
              >
                <i className={`${cat.icon} text-xs`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          {/* Articles Grid */}
          {categoryArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {categoryArticles.map((article) => {
                const cat = categories.find((c) => c.slug === article.category);
                return (
                  <ArticleCard
                    key={article.slug}
                    slug={article.slug}
                    title={article.title}
                    excerpt={article.excerpt}
                    coverImage={article.coverImage}
                    category={article.category}
                    categoryName={cat?.name || article.category}
                    readTime={article.readTime}
                    publishedAt={article.publishedAt}
                  />
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-foreground-500 mb-4">该分类下暂无文章</p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                返回首页
              </Link>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}